import React, { useState, useEffect, useCallback } from 'react';
import { Box, Card, CardContent, Typography, Button, IconButton, Chip, Tabs, Tab, FormControl, InputLabel, Select, MenuItem, TextField, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Tooltip, Alert, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, List, ListItemText, ListItemIcon, Divider, Stack, Avatar, CardActions} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Settings,
  Palette,
  Extension,
  Security,
  Email,
  Backup,
  Monitor,
  Api,
  Save,
  Refresh,
  Download,
  Upload,
  PlayArrow,
  Visibility,
  Edit,
  Delete,
  Add,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info,
  Build,
  Storage,
  NetworkCheck,
  Shield,
  Lock,
  Key,
  Webhook,
  CloudUpload,
  CloudDownload,
  Restore,
  BugReport,
  TrendingUp,
  Timeline,
  Payment,
  Sms} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { 
  masterSettingsService, 
  MasterSettings,
  BrandingSettings,
  IntegrationSettings,
  SecuritySettings,
  SystemNotificationSettings,
  BackupSettings,
  MonitoringSettings,
  ApiSettings} from '@services/masterSettingsService';
import KPICard from '@shared/KPICard';
import StatusChip from '@shared/StatusChip';
import { formatForDisplay, formatDateOnly, formatTimeOnly } from '@utils/dateFormatter';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SettingsManagement: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Setari
  const [masterSettings, setMasterSettings] = useState<MasterSettings | null>(null);
  const [brandingSettings, setBrandingSettings] = useState<BrandingSettings | null>(null);
  const [integrations, setIntegrations] = useState<IntegrationSettings[]>([]);
  const [securitySettings, setSecuritySettings] = useState<SecuritySettings | null>(null);
  const [notificationSettings, setNotificationSettings] = useState<SystemNotificationSettings | null>(null);
  const [backupSettings, setBackupSettings] = useState<BackupSettings | null>(null);
  const [monitoringSettings, setMonitoringSettings] = useState<MonitoringSettings | null>(null);
  const [apiSettings, setApiSettings] = useState<ApiSettings | null>(null);

  // Dialogs
  const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<IntegrationSettings | null>(null);

  // Incarcare setari
  const loadMasterSettings = useCallback(async () => {
    try {
      const data = await masterSettingsService.getMasterSettings();
      setMasterSettings(data);
    } catch (err) {
      console.error('Error loading master settings:', err);
    }
  }, []);

  const loadBrandingSettings = useCallback(async () => {
    try {
      const data = await masterSettingsService.getBrandingSettings();
      setBrandingSettings(data);
    } catch (err) {
      console.error('Error loading branding settings:', err);
    }
  }, []);

  const loadIntegrations = useCallback(async () => {
    try {
      const data = await masterSettingsService.getIntegrations();
      setIntegrations(data.integrations);
    } catch (err) {
      console.error('Error loading integrations:', err);
    }
  }, []);

  const loadSecuritySettings = useCallback(async () => {
    try {
      const data = await masterSettingsService.getSecuritySettings();
      setSecuritySettings(data);
    } catch (err) {
      console.error('Error loading security settings:', err);
    }
  }, []);

  const loadNotificationSettings = useCallback(async () => {
    try {
      const data = await masterSettingsService.getSystemNotificationSettings();
      setNotificationSettings(data);
    } catch (err) {
      console.error('Error loading notification settings:', err);
    }
  }, []);

  const loadBackupSettings = useCallback(async () => {
    try {
      const data = await masterSettingsService.getBackupSettings();
      setBackupSettings(data);
    } catch (err) {
      console.error('Error loading backup settings:', err);
    }
  }, []);

  const loadMonitoringSettings = useCallback(async () => {
    try {
      const data = await masterSettingsService.getMonitoringSettings();
      setMonitoringSettings(data);
    } catch (err) {
      console.error('Error loading monitoring settings:', err);
    }
  }, []);

  const loadApiSettings = useCallback(async () => {
    try {
      const data = await masterSettingsService.getApiSettings();
      setApiSettings(data);
    } catch (err) {
      console.error('Error loading API settings:', err);
    }
  }, []);

  useEffect(() => {
    loadMasterSettings();
  }, [loadMasterSettings]);

  useEffect(() => {
    if (activeTab === 1) {
      loadBrandingSettings();
    } else if (activeTab === 2) {
      loadIntegrations();
    } else if (activeTab === 3) {
      loadSecuritySettings();
    } else if (activeTab === 4) {
      loadNotificationSettings();
    } else if (activeTab === 5) {
      loadBackupSettings();
    } else if (activeTab === 6) {
      loadMonitoringSettings();
    } else if (activeTab === 7) {
      loadApiSettings();
    }
  }, [activeTab, loadBrandingSettings, loadIntegrations, loadSecuritySettings, loadNotificationSettings, loadBackupSettings, loadMonitoringSettings, loadApiSettings]);

  // Handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSaveMasterSettings = async () => {
    if (!masterSettings) return;
    
    try {
      await masterSettingsService.updateMasterSettings(masterSettings);
      setSuccess('Setarile master au fost salvate cu succes');
    } catch (err) {
      console.error('Error saving master settings:', err);
      setError('Eroare la salvarea setarilor master');
    }
  };

  const handleSaveBrandingSettings = async () => {
    if (!brandingSettings) return;
    
    try {
      await masterSettingsService.updateBrandingSettings(brandingSettings);
      setSuccess('Setarile de branding au fost salvate cu succes');
    } catch (err) {
      console.error('Error saving branding settings:', err);
      setError('Eroare la salvarea setarilor de branding');
    }
  };

  const handleTestIntegration = async (id: string) => {
    try {
      const result = await masterSettingsService.testIntegration(id);
      if (result.success) {
        setSuccess('Integrarea functioneaza corect');
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error testing integration:', err);
      setError('Eroare la testarea integrarii');
    }
  };

  const getIntegrationIcon = (type: string) => {
    switch (type) {
      case 'payment': return <Payment />;
      case 'email': return <Email />;
      case 'sms': return <Sms />;
      case 'analytics': return <TrendingUp />;
      case 'storage': return <Storage />;
      case 'backup': return <Backup />;
      case 'monitoring': return <Monitor />;
      default: return <Extension />;
    }
  };

  const getIntegrationColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'pending': return 'warning';
      case 'disabled': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Setari Master
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configurare sistem, branding si integrari
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              loadMasterSettings();
              loadBrandingSettings();
              loadIntegrations();
            }}
            disabled={loading}
          >
            Reimprospateaza
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={() => masterSettingsService.exportSettings()}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Success/Error Alerts */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KPICard
            title="Sistem"
            value={masterSettings?.systemName || 'N/A'}
            icon={<Settings />}
            color="primary"
            subtitle={`v${masterSettings?.systemVersion || '1.0.0'}`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KPICard
            title="Integrari Active"
            value={integrations.filter(i => i.isActive).length}
            icon={<Extension />}
            color="success"
            subtitle={`din ${integrations.length}`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KPICard
            title="Ultimul Backup"
            value={backupSettings?.lastBackup ? formatDateOnly(backupSettings.lastBackup) : 'N/A'}
            icon={<Backup />}
            color="info"
            subtitle={backupSettings?.status || 'N/A'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KPICard
            title="Monitorizare"
            value={monitoringSettings?.enabled ? 'Activata' : 'Dezactivata'}
            icon={<Monitor />}
            color={monitoringSettings?.enabled ? 'success' : 'warning'}
            subtitle="Status sistem"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="settings tabs" variant="scrollable">
          <Tab label="General" icon={<Settings />} iconPosition="start" />
          <Tab label="Branding" icon={<Palette />} iconPosition="start" />
          <Tab label="Integrari" icon={<Extension />} iconPosition="start" />
          <Tab label="Securitate" icon={<Security />} iconPosition="start" />
          <Tab label="Notificari" icon={<Email />} iconPosition="start" />
          <Tab label="Backup" icon={<Backup />} iconPosition="start" />
          <Tab label="Monitorizare" icon={<Monitor />} iconPosition="start" />
          <Tab label="API" icon={<Api />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <LinearProgress sx={{ width: '100%' }} />
        </Box>
      )}

      {/* General Tab */}
      <TabPanel value={activeTab} index={0}>
        {masterSettings && (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Informatii Sistem
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Nume Sistem"
                      value={masterSettings.systemName}
                      onChange={(e) => setMasterSettings({
                        ...masterSettings,
                        systemName: e.target.value
                      })}
                    />
                    <TextField
                      fullWidth
                      label="Versiune"
                      value={masterSettings.systemVersion}
                      onChange={(e) => setMasterSettings({
                        ...masterSettings,
                        systemVersion: e.target.value
                      })}
                    />
                    <FormControl fullWidth>
                      <InputLabel>Mediu</InputLabel>
                      <Select
                        value={masterSettings.environment}
                        label="Mediu"
                        onChange={(e) => setMasterSettings({
                          ...masterSettings,
                          environment: e.target.value as any
                        })}
                      >
                        <MenuItem value="development">Development</MenuItem>
                        <MenuItem value="staging">Staging</MenuItem>
                        <MenuItem value="production">Production</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={masterSettings.maintenanceMode}
                          onChange={(e) => setMasterSettings({
                            ...masterSettings,
                            maintenanceMode: e.target.checked
                          })}
                        />
                      }
                      label="Mod Mentenanta"
                    />
                  </Stack>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSaveMasterSettings}
                  >
                    Salveaza
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Branding Tab */}
      <TabPanel value={activeTab} index={1}>
        {brandingSettings && (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Culori si Stil
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Culoare Principala"
                      type="color"
                      value={brandingSettings.primaryColor}
                      onChange={(e) => setBrandingSettings({
                        ...brandingSettings,
                        primaryColor: e.target.value
                      })}
                    />
                    <TextField
                      fullWidth
                      label="Culoare Secundara"
                      type="color"
                      value={brandingSettings.secondaryColor}
                      onChange={(e) => setBrandingSettings({
                        ...brandingSettings,
                        secondaryColor: e.target.value
                      })}
                    />
                    <TextField
                      fullWidth
                      label="Familie Font"
                      value={brandingSettings.fontFamily}
                      onChange={(e) => setBrandingSettings({
                        ...brandingSettings,
                        fontFamily: e.target.value
                      })}
                    />
                    <FormControl fullWidth>
                      <InputLabel>Tema</InputLabel>
                      <Select
                        value={brandingSettings.theme}
                        label="Tema"
                        onChange={(e) => setBrandingSettings({
                          ...brandingSettings,
                          theme: e.target.value as any
                        })}
                      >
                        <MenuItem value="light">Luminos</MenuItem>
                        <MenuItem value="dark">Intunecat</MenuItem>
                        <MenuItem value="auto">Automat</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSaveBrandingSettings}
                  >
                    Salveaza
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Integrari Tab */}
      <TabPanel value={activeTab} index={2}>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditingIntegration(null);
              setIntegrationDialogOpen(true);
            }}
          >
            Adauga Integrare
          </Button>
        </Box>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nume</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ultima Sincronizare</TableCell>
                <TableCell>Actiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {integrations.map((integration) => (
                <TableRow key={integration.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getIntegrationIcon(integration.type)}
                      <Box sx={{ ml: 2 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {integration.name}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={integration.type} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {integration.provider}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip 
                        label={integration.isActive ? 'ACTIV' : 'INACTIV'}
                        color={integration.isActive ? 'success' : 'default'}
                        variant="outlined"
                      />
                      <Chip 
                        label={integration.syncStatus.toUpperCase()}
                        color={integration.syncStatus === 'success' ? 'success' : 
                               integration.syncStatus === 'error' ? 'error' :
                               integration.syncStatus === 'pending' ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {integration.lastSync ? formatForDisplay(integration.lastSync) : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Testeaza">
                        <IconButton
                          size="small"
                          onClick={() => handleTestIntegration(integration.id)}
                        >
                          <PlayArrow />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editeaza">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingIntegration(integration);
                            setIntegrationDialogOpen(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sterge">
                        <IconButton size="small">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Alte taburi vor fi implementate similar */}
    </Box>
  );
};

export default SettingsManagement;
